import { useEffect, useState } from 'react'
import { api, type Faktorzustand } from '../api'
import { QrKode, Schluessel } from '../komponenten/QrKode'

/* Der zweite Faktor am eigenen Konto.
 *
 * Es geht hier immer nur um das **eigene** Konto. Einen zweiten Faktor,
 * den ein anderer einrichten oder abschalten kann, ist keiner — und
 * deshalb steht auf dieser Seite nirgends eine fremde Kennung.
 *
 * Wer ihn abschalten will, tippt sein Passwort. Ein offener Rechner in
 * der Küche ist kein erdachter Fall.
 */

function Codes({ codes }: { codes: string[] }) {
  return (
    <div className="einladung">
      <b>Ersatzcodes</b>
      <p className="dezent">
        Jeder gilt <b>genau einmal</b>. Sie stehen <b>jetzt und nie
        wieder</b> hier — danach nur noch ihre Prüfsumme in der
        Datenbank.
      </p>
      <ol className="ersatzcodes">
        {codes.map((c) => <li key={c}><code>{c}</code></li>)}
      </ol>
      <div className="knopfreihe">
        <button type="button" onClick={() => {
          void navigator.clipboard?.writeText(codes.join('\n'))
            .catch(() => undefined)
        }}>Kopieren</button>
        <button type="button" onClick={() => window.print()}>Drucken</button>
      </div>
    </div>
  )
}

export function Sicherheit() {
  const [zustand, setZustand] = useState<Faktorzustand | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [adresse, setAdresse] = useState<string | null>(null)
  const [kennzahl, setKennzahl] = useState('')
  const [passwort, setPasswort] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)

  const laden = () => {
    api.faktor.zustand().then(setZustand).catch((e) => setFehler(e.message))
  }
  useEffect(laden, [])

  const sagen = (e: unknown) =>
    setFehler(e instanceof Error ? e.message : String(e))

  const beginnen = async () => {
    setFehler(null); setCodes(null)
    try { setAdresse((await api.faktor.beginnen()).einrichtung) }
    catch (e) { sagen(e) }
  }

  const bestaetigen = async (e: React.FormEvent) => {
    e.preventDefault(); setFehler(null)
    try {
      const a = await api.faktor.bestaetigen(kennzahl)
      setCodes(a.ersatzcodes); setAdresse(null); setKennzahl(''); laden()
    } catch (err) { sagen(err) }
  }

  const abschalten = async () => {
    setFehler(null)
    try { await api.faktor.abschalten(passwort); setPasswort(''); setCodes(null); laden() }
    catch (e) { sagen(e) }
  }

  const neueCodes = async () => {
    setFehler(null)
    try { setCodes((await api.faktor.ersatzcodes(passwort)).ersatzcodes)
          setPasswort(''); laden() }
    catch (e) { sagen(e) }
  }

  if (fehler && !zustand) return <div className="fehler">{fehler}</div>
  if (!zustand) return <div className="laedt">Wird geladen …</div>

  return (
    <div className="backoffice">
      {fehler && <div className="fehler" role="alert">{fehler}</div>}

      <div className="karte">
        <div className="karte-kopf">
          <h3>Zweiter Faktor</h3>
          <span className={zustand.gilt ? 'zustand an' : 'zustand'}>
            {zustand.gilt ? 'eingerichtet' : 'nicht eingerichtet'}
          </span>
        </div>

        <p className="dezent">
          Eine Authenticator-App auf dem Telefon zeigt alle dreißig
          Sekunden eine sechsstellige Zahl. Ohne sie kommt niemand
          hinein, der nur das Passwort hat.
          {zustand.pflicht && (
            <> Für Ihre Stufe ist er <b>Pflicht</b> — sie sieht alle
            Mandanten.</>)}
        </p>

        {codes && <Codes codes={codes} />}

        {!zustand.gilt && !adresse && (
          <div className="knopfreihe">
            <button type="button" onClick={() => void beginnen()}>
              Einrichten
            </button>
          </div>
        )}

        {!zustand.gilt && adresse && (
          <form onSubmit={bestaetigen} className="einrichtung">
            <p className="dezent">
              Mit der App abfotografieren, dann die Zahl abtippen, die
              sie zeigt. <b>Erst dann</b> gilt er — wer hier abbricht,
              hat sich nicht ausgesperrt.
            </p>
            <QrKode inhalt={adresse} />
            <Schluessel adresse={adresse} />
            <div className="knopfreihe">
              <input inputMode="numeric" placeholder="000000" required
                     autoComplete="one-time-code" value={kennzahl}
                     onChange={(e) => setKennzahl(e.target.value)} />
              <button type="submit">Bestätigen</button>
              <button type="button" className="verweis"
                      onClick={() => setAdresse(null)}>Abbrechen</button>
            </div>
          </form>
        )}

        {zustand.gilt && (
          <>
            <p className="dezent">
              Noch <b>{zustand.ersatzcodes}</b> ungenutzte Ersatzcodes.
              {zustand.ersatzcodes <= 2 && (
                <> Das wird knapp — neue holen, solange Sie noch
                hineinkommen.</>)}
            </p>
            <div className="knopfreihe">
              <input type="password" placeholder="Ihr Passwort"
                     autoComplete="current-password" value={passwort}
                     onChange={(e) => setPasswort(e.target.value)} />
              <button type="button" disabled={!passwort}
                      onClick={() => void neueCodes()}>
                Neue Ersatzcodes
              </button>
              {!zustand.pflicht && (
                <button type="button" disabled={!passwort}
                        onClick={() => void abschalten()}>
                  Abschalten
                </button>
              )}
            </div>
            <p className="dezent">
              Neue Codes machen die alten ungültig — der alte Zettel
              gilt danach nicht mehr.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
